import { useState } from 'react';
import { motion } from 'framer-motion';
import { UserCog, Plus, Search, Phone, Mail, MapPin, Clock, Edit3, Trash2, X, Briefcase, Star } from 'lucide-react';
import { useAdminStore } from '../store/adminStore';
import { formatCurrency, formatDate, getRelativeTime } from '../utils/helpers';
import { toast } from '../components/Toast';

export default function StaffPage() {
  const { staff, addStaff, updateStaff, deleteStaff } = useAdminStore();
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showAdd, setShowAdd] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', phone: '', email: '', role: 'technician' as const, salary: 0 });

  const filtered = staff.filter(s => {
    const matchSearch = s.name.toLowerCase().includes(search.toLowerCase()) || s.phone.includes(search);
    const matchRole = roleFilter === 'all' || s.role === roleFilter;
    return matchSearch && matchRole;
  });

  const handleAdd = () => {
    if (!newMember.name.trim()) { toast.error('Validation Error', 'Staff name is required.'); return; }
    if (!newMember.phone.trim()) { toast.error('Validation Error', 'Phone number is required.'); return; }
    addStaff({
      id: `STF-${Date.now()}`, ...newMember, isActive: true, joinDate: new Date().toISOString(),
    });
    toast.success('Staff Added', `${newMember.name} has been added to the team.`);
    setNewMember({ name: '', phone: '', email: '', role: 'technician', salary: 0 });
    setShowAdd(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-[24px] font-semibold text-gray-900 tracking-tight">Staff Management</h1>
          <p className="text-[13px] text-gray-400 mt-0.5">{staff.length} team members</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="h-10 px-5 rounded-xl bg-gray-900 text-white text-[13px] font-medium hover:bg-gray-800 flex items-center gap-2">
          <Plus className="w-4 h-4" />Add Member
        </button>
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search staff..."
            className="w-full h-10 pl-10 pr-4 rounded-xl bg-white border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" />
        </div>
        <div className="flex bg-white border border-gray-200/60 rounded-xl overflow-hidden">
          {['all', 'technician', 'installer', 'office', 'manager'].map(r => (
            <button key={r} onClick={() => setRoleFilter(r)}
              className={`px-3 h-10 text-[12px] font-medium capitalize transition-all ${roleFilter === r ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>
              {r}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.length === 0 && (
          <div className="col-span-full bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-12 text-center">
            <UserCog className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-[15px] font-semibold text-gray-500">
              {search || roleFilter !== 'all' ? 'No staff match your search' : 'No team members yet'}
            </p>
            <p className="text-[12px] text-gray-400 mt-1">Add your first team member to get started.</p>
          </div>
        )}
        {filtered.map((member, i) => (
          <motion.div key={member.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
            className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-5 hover:shadow-lg hover:shadow-gray-200/40 transition-all group">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center text-white text-[14px] font-semibold">
                {member.name.split(' ').map(n => n[0]).join('')}
              </div>
              <div className="flex-1">
                <h3 className="text-[14px] font-semibold text-gray-900">{member.name}</h3>
                <span className="px-2 py-0.5 rounded-full bg-blue-50 text-[10px] font-medium text-blue-600 capitalize">{member.role}</span>
              </div>
              <span className={`w-2.5 h-2.5 rounded-full ${member.isActive ? 'bg-emerald-400' : 'bg-gray-300'}`} />
            </div>
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-[12px] text-gray-500"><Phone className="w-3.5 h-3.5 text-gray-400" />{member.phone}</div>
              {member.email && <div className="flex items-center gap-2 text-[12px] text-gray-500"><Mail className="w-3.5 h-3.5 text-gray-400" />{member.email}</div>}
              {member.salary && <div className="flex items-center gap-2 text-[12px] text-gray-500"><Briefcase className="w-3.5 h-3.5 text-gray-400" />{formatCurrency(member.salary)}/mo</div>}
            </div>
            {member.gpsCheckIn && (
              <div className="p-3 bg-emerald-50/80 rounded-xl mb-3">
                <div className="flex items-center gap-1.5 text-[11px] text-emerald-600 font-medium"><MapPin className="w-3 h-3" />Checked in</div>
                <div className="text-[11px] text-emerald-500 mt-0.5">{member.gpsCheckIn.location} · {getRelativeTime(member.gpsCheckIn.timestamp)}</div>
              </div>
            )}
            {member.skills && member.skills.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {member.skills.map(skill => (
                  <span key={skill} className="px-2 py-0.5 rounded-full bg-gray-100 text-[10px] font-medium text-gray-600">{skill}</span>
                ))}
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md bg-white rounded-3xl shadow-2xl border border-gray-200/60 p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-[18px] font-semibold text-gray-900">Add Team Member</h2>
              <button onClick={() => setShowAdd(false)} className="p-1.5 rounded-xl hover:bg-gray-100"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="space-y-4">
              <div><label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Name *</label>
                <input value={newMember.name} onChange={e => setNewMember({ ...newMember, name: e.target.value })}
                  className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Phone *</label>
                  <input value={newMember.phone} onChange={e => setNewMember({ ...newMember, phone: e.target.value })}
                    className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" /></div>
                <div><label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Role</label>
                  <select value={newMember.role} onChange={e => setNewMember({ ...newMember, role: e.target.value as any })}
                    className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5">
                    <option value="technician">Technician</option><option value="installer">Installer</option>
                    <option value="office">Office</option><option value="manager">Manager</option>
                  </select></div>
              </div>
              <div><label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Monthly Salary</label>
                <input type="number" value={newMember.salary || ''} onChange={e => setNewMember({ ...newMember, salary: Number(e.target.value) })}
                  className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" /></div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowAdd(false)} className="h-10 px-5 rounded-xl bg-gray-100 text-gray-600 text-[13px] font-medium hover:bg-gray-200">Cancel</button>
              <button onClick={handleAdd} disabled={!newMember.name || !newMember.phone}
                className="h-10 px-5 rounded-xl bg-gray-900 text-white text-[13px] font-medium hover:bg-gray-800 disabled:opacity-50">Add Member</button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
